import React from 'react';
import { newPaymentCondition, percentLabel, type PaymentCondition, type PaymentStage, type LoanTrigger } from '../office/paymentCondition';
import './bookingForm/bookingForm.css';

// Rendered inside the booking form dialog's .bf scope; the live roadmap is shown by BookingPlanPanel beside it.
type Props = { value: PaymentCondition; onChange: (value: PaymentCondition) => void; bookingDate: string; unitAmount?: number };

const rupees = (value: number) => `₹${Math.round(value).toLocaleString('en-IN')}`;

export default function PaymentConditionEditor({ value, onChange, unitAmount = 0 }: Props) {
  const stages = value.stages;
  const updateStage = (index: number, patch: Partial<PaymentStage>) => onChange({ ...value, stages: stages.map((stage, i) => i === index ? { ...stage, ...patch } : stage) });
  const dp = stages[0];
  const remaining = stages[1];
  const share = (percent: number) => `${percentLabel(Number(percent) || 0)}${unitAmount > 0 ? ` · ${rupees((unitAmount * (Number(percent) || 0)) / 100)}` : ''}`;
  const id = (name: string, index = 0) => `bf-pc-${name}-${index}`;

  const percentField = (label: string, fieldId: string, numericValue: number, change: (next: number) => void) => (
    <div className="bf-f">
      <label htmlFor={fieldId}>{label}</label>
      <input id={fieldId} className="bf-inp num" type="number" inputMode="decimal" min={0} max={100} step={0.01} value={Number.isFinite(numericValue) ? numericValue : ''} onChange={(event) => change(Number(event.target.value))} />
    </div>
  );
  const timingFields = (stage: PaymentStage, index: number) => (
    <>
      <div className="bf-f">
        <label htmlFor={id('due', index)}>Due</label>
        <select id={id('due', index)} className="bf-inp" value={stage.timing} onChange={(event) => updateStage(index, { timing: event.target.value as PaymentStage['timing'], count: event.target.value === 'possession' || event.target.value === 'specific-month' ? undefined : stage.count || 1 })}>
          <option value="days">Within days</option>
          <option value="months">Within months</option>
          {index !== 0 && <option value="monthly">Monthly instalments</option>}
          {index !== 0 && <option value="specific-month">Specific month</option>}
          {index !== 0 && <option value="possession">On possession</option>}
        </select>
      </div>
      {stage.timing === 'specific-month' && (
        <div className="bf-f">
          <label htmlFor={id('month', index)}>Payment month</label>
          <input id={id('month', index)} className="bf-inp" type="month" value={stage.month || ''} onChange={(event) => updateStage(index, { month: event.target.value })} />
        </div>
      )}
      {stage.timing !== 'possession' && stage.timing !== 'specific-month' && (
        <div className="bf-f">
          <label htmlFor={id('count', index)}>{stage.timing === 'monthly' ? 'Number of months' : stage.timing === 'days' ? 'Days' : 'Months'}</label>
          <input id={id('count', index)} className="bf-inp num" type="number" inputMode="numeric" min={1} step={1} value={stage.count ?? ''} onChange={(event) => updateStage(index, { count: Number(event.target.value) })} />
        </div>
      )}
    </>
  );
  const changeLoanTrigger = (kind: LoanTrigger['kind']) => {
    const trigger: LoanTrigger = kind === 'month' ? { kind, month: '' } : kind === 'after-percent' ? { kind, percent: 50 } : { kind };
    onChange({ ...value, loan: { ...value.loan, trigger } });
  };

  return <>
    <section className="bf-sec" aria-label="Payment plan">
      <div className="bf-sec-h">
        <b>Payment plan</b>
        <div className="bf-seg" role="group" aria-label="Plan type">
          <button type="button" aria-pressed={value.mode === 'basic'} onClick={() => {
            const basic = newPaymentCondition();
            onChange({ ...basic, stages: value.mode === 'basic' ? stages : basic.stages, loan: value.loan });
          }}>Basic</button>
          <button type="button" aria-pressed={value.mode === 'advanced'} onClick={() => onChange({ ...value, mode: 'advanced' })}>Advanced</button>
        </div>
      </div>
      {value.mode === 'basic' ? <>
        <div className="bf-box">
          <div className="bf-box-h"><b>Down payment</b><span className="bf-chip num">{share(dp.percent)}</span></div>
          <div className="bf-grid3">
            {percentField('DP %', id('dp'), dp.percent, (percent) => onChange({ ...value, stages: [{ ...dp, percent }, { ...remaining, percent: Number((100 - percent).toFixed(2)) }] }))}
            {timingFields(dp, 0)}
          </div>
        </div>
        <div className="bf-box">
          <div className="bf-box-h"><b>Remaining</b><span className="bf-chip num">{share(remaining.percent)}</span></div>
          <div className="bf-grid2">{timingFields(remaining, 1)}</div>
          {remaining.timing === 'monthly' && <span className="bf-hint">First instalment starts after the DP deadline.</span>}
        </div>
      </> : <>
        <span className="bf-hint">Stages run one after another and must total 100%. The first stage is the down payment.</span>
        {stages.map((stage, index) => (
          <div className="bf-box" key={stage.id}>
            <div className="bf-box-h">
              <input className="bf-inp bf-stage-name" aria-label={`Stage ${index + 1} name`} placeholder="Stage name" value={stage.label} onChange={(event) => updateStage(index, { label: event.target.value })} />
              <span className="bf-chip num">{share(stage.percent)}</span>
              <button type="button" className="bf-link bad" disabled={stages.length <= 2 || index === 0} onClick={() => onChange({ ...value, stages: stages.filter((_, i) => i !== index) })}>Remove</button>
            </div>
            <div className="bf-grid3">
              {percentField('Payment %', id('pct', index), stage.percent, (percent) => updateStage(index, { percent }))}
              {timingFields(stage, index)}
            </div>
          </div>
        ))}
        <div><button type="button" className="bf-btn sm" onClick={() => onChange({ ...value, stages: [...stages, { id: `stage-${Date.now()}`, label: 'Additional stage', percent: 0, timing: 'months', count: 1 }] })}>+ Add stage</button></div>
      </>}
    </section>

    <section className="bf-sec" aria-label="Loan">
      <div className="bf-sec-h"><b>Loan</b><span>Optional</span></div>
      <div className="bf-box">
        <div className="bf-toggle">
          <span>Buyer plans a home loan</span>
          <label className="bf-switch">
            <input type="checkbox" checked={Boolean(value.loan)} onChange={() => onChange({ ...value, loan: value.loan ? undefined : { requestedAmount: 0, trigger: { kind: 'after-dp' } } })} aria-label="Buyer plans a home loan" />
            <span className="track"><span className="knob" /></span>
          </label>
        </div>
        {value.loan && <>
          {value.loan.requestedPercent != null && value.loan.requestedPercent > 0 && value.loan.requestedAmount == null && <div className="bf-note info">Saved earlier as {percentLabel(value.loan.requestedPercent)}. Enter a rupee amount to replace it.</div>}
          <div className="bf-grid2">
            <div className="bf-f">
              <label htmlFor="bf-pc-loan">Total loan requested</label>
              <div className="bf-pre"><span>₹</span><input id="bf-pc-loan" className="bf-inp num" type="number" inputMode="numeric" min={1} step={1} value={value.loan.requestedAmount || ''} onChange={(event) => onChange({ ...value, loan: { ...value.loan!, requestedAmount: Number(event.target.value), requestedPercent: undefined } })} /></div>
            </div>
            <div className="bf-f">
              <label htmlFor="bf-pc-when">When the buyer applies</label>
              <select id="bf-pc-when" className="bf-inp" value={value.loan.trigger.kind} onChange={(event) => changeLoanTrigger(event.target.value as LoanTrigger['kind'])}>
                <option value="month">In a specific month</option>
                <option value="after-dp">After DP is received</option>
                <option value="after-percent">After a paid percentage</option>
                <option value="possession">At possession</option>
              </select>
            </div>
          </div>
          {value.loan.trigger.kind === 'month' && (
            <div className="bf-f">
              <label htmlFor="bf-pc-lm">Application month</label>
              <input id="bf-pc-lm" className="bf-inp" type="month" value={value.loan.trigger.month} onChange={(event) => onChange({ ...value, loan: { ...value.loan!, trigger: { kind: 'month', month: event.target.value } } })} />
            </div>
          )}
          {value.loan.trigger.kind === 'after-percent' && percentField('After paid %', 'bf-pc-lp', value.loan.trigger.percent, (percent) => onChange({ ...value, loan: { ...value.loan!, trigger: { kind: 'after-percent', percent } } }))}
          <span className="bf-hint">May include furniture funding, so it can be more than the amount still due. The bank decides the actual loan; the 100% plan does not change.</span>
        </>}
      </div>
    </section>
  </>;
}
